import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PaDataTable,
  PaEmptyState,
  PaFilterBar,
  PaFilterChip,
  PaNavigationContextBanner,
  PaPageHeader,
  PaPagination,
  PaStatusBadge,
  PaTabs,
} from "components/platform-admin";
import { getNavigationContext } from "pages/PlatformAdmin/platformAdminNavigation.utils";
import PlatformAdminLayout from "pages/PlatformAdmin/layout/PlatformAdminLayout";
import PaAccessDenied from "pages/PlatformAdmin/layout/PaAccessDenied";
import PaRelativeTime from "pages/PlatformAdmin/PaRelativeTime";
import { platformRoleAccess } from "helpers/RolesPermission";
import { usePlatformAdmin } from "context/PlatformAdminContext";
import GhostStartModal from "pages/PlatformAdmin/Ghost/GhostStartModal";
import { USER_ROLE } from "Constants/constant";
import {
  fetchPlatformDashboardStats,
  fetchPlatformTenantGeography,
  fetchPlatformTenantStateGeography,
  fetchPlatformTenants,
} from "store/services/platformAdmin.services";
import { URL_PLATFORM_ADMIN_TENANTS } from "store/services/URL";
import { getPaginationObj } from "Utils/utils";
import TenantListRowActions from "./TenantListRowActions";
import TenantsGeographyView from "./TenantsGeographyView";
import TenantsListSummary from "./TenantsListSummary";
import {
  applyTenantWidgetPreset,
  buildTenantsListQuery,
  buildTenantsListSearchParams,
  CARE_SECTOR_OPTIONS,
  DEFAULT_TENANT_FILTERS,
  formatCareSectorLabel,
  getTenantStatusBadge,
  getTenantsListPageSubtitle,
  hasActiveTenantFilters,
  parseTenantFiltersFromSearchParams,
  parseTenantsListView,
  serializeTenantsListSearchParams,
  tenantFiltersEqual,
  TENANTS_LIST_VIEWS,
  TENANTS_LIST_VIEW_OPTIONS,
  TENANT_STATUS_OPTIONS,
  toggleFilterValue,
} from "./tenantsList.utils";
import { ALL_AUSTRALIAN_STATE_CODES } from "./tenantGeography.utils";
import { buildGeographyViewPath } from "./tenantGeographyState.utils";
import { buildTenantListBillingCell } from "./tenantListBilling.utils";

const TenantsList = () => {
  const canViewTenants = platformRoleAccess("platform.tenant.list");
  const canViewDashboard = platformRoleAccess("platform.dashboard.view");
  const canViewBilling = platformRoleAccess("platform.billing.view");
  const canEnterTenant = platformRoleAccess("platform.tenant.enter");
  const canStartGhost = platformRoleAccess("platform.ghost.start");
  const { enterTenant, tenantContext, startGhost, ghostSubmitting } = usePlatformAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const navigationContext = useMemo(
    () => getNavigationContext(searchParams.get("focus")),
    [searchParams]
  );
  const activeView = useMemo(() => parseTenantsListView(searchParams), [searchParams]);
  const isGeographyView = activeView === TENANTS_LIST_VIEWS.GEOGRAPHY;

  const [ghostTarget, setGhostTarget] = useState(null);
  const [filters, setFilters] = useState(() =>
    parseTenantFiltersFromSearchParams(searchParams)
  );
  const [searchInput, setSearchInput] = useState(() => filters.search);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(getPaginationObj());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryStats, setSummaryStats] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(canViewDashboard);
  const [attentionCount, setAttentionCount] = useState(0);
  const [geography, setGeography] = useState(null);
  const [geographyLoading, setGeographyLoading] = useState(false);
  const [geographyError, setGeographyError] = useState(null);
  const [stateGeography, setStateGeography] = useState(null);
  const [stateGeographyLoading, setStateGeographyLoading] = useState(false);
  const [stateGeographyError, setStateGeographyError] = useState(null);

  const selectedGeographyState = useMemo(() => {
    if (!isGeographyView) {
      return null;
    }

    const stateCode = (searchParams.get("state") || "").toUpperCase();

    return ALL_AUSTRALIAN_STATE_CODES.includes(stateCode) ? stateCode : null;
  }, [isGeographyView, searchParams]);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    if (!canViewDashboard) {
      setSummaryLoading(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setSummaryLoading(true);

      try {
        const payload = await fetchPlatformDashboardStats();
        if (cancelled) {
          return;
        }

        setSummaryStats(payload?.stats ?? null);
        setAttentionCount(
          (payload?.trials_expiring?.length || 0) + (payload?.failed_payments?.length || 0)
        );
      } catch {
        if (!cancelled) {
          setSummaryStats(null);
          setAttentionCount(0);
        }
      } finally {
        if (!cancelled) {
          setSummaryLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canViewDashboard]);

  useEffect(() => {
    if (!canViewTenants || !isGeographyView) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setGeographyLoading(true);
      setGeographyError(null);

      try {
        const payload = await fetchPlatformTenantGeography();
        if (!cancelled) {
          setGeography(payload);
        }
      } catch (err) {
        if (!cancelled) {
          setGeography(null);
          setGeographyError(err?.message || "Failed to load tenant geography");
        }
      } finally {
        if (!cancelled) {
          setGeographyLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canViewTenants, isGeographyView]);

  useEffect(() => {
    if (!canViewTenants || !isGeographyView || !selectedGeographyState) {
      setStateGeography(null);
      setStateGeographyLoading(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setStateGeographyLoading(true);
      setStateGeographyError(null);

      try {
        const payload = await fetchPlatformTenantStateGeography(selectedGeographyState);
        if (!cancelled) {
          setStateGeography(payload);
        }
      } catch (err) {
        if (!cancelled) {
          setStateGeography(null);
          setStateGeographyError(err?.message || "Failed to load state geography");
        }
      } finally {
        if (!cancelled) {
          setStateGeographyLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canViewTenants, isGeographyView, selectedGeographyState]);

  const handleSelectGeographyState = useCallback(
    (stateCode) => {
      navigate(buildGeographyViewPath({ stateCode }));
    },
    [navigate]
  );

  const handleClearGeographyState = useCallback(() => {
    navigate(buildGeographyViewPath());
  }, [navigate]);

  useEffect(() => {
    if (!canViewTenants || isGeographyView) {
      if (isGeographyView) {
        setLoading(false);
      }
      return undefined;
    }

    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const endpoint = buildTenantsListQuery(filtersRef.current, URL_PLATFORM_ADMIN_TENANTS);
        const payload = await fetchPlatformTenants(endpoint, {
          signal: controller.signal,
        });
        const tenants = payload?.tenants;
        setRows(Array.isArray(tenants?.data) ? tenants.data : []);
        setPagination(getPaginationObj(tenants, filtersRef.current));
      } catch (err) {
        if (controller.signal.aborted || err?.code === "ERR_CANCELED") {
          return;
        }

        setRows([]);
        setError(err?.message || "Failed to load tenants");
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    })();

    return () => controller.abort();
  }, [canViewTenants, filters, isGeographyView]);

  useEffect(() => {
    if (isGeographyView) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput) {
          return prev;
        }

        return { ...prev, search: searchInput, page: 1 };
      });
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchInput, isGeographyView]);

  useEffect(() => {
    if (isGeographyView) {
      return;
    }

    const parsed = parseTenantFiltersFromSearchParams(searchParams);

    setFilters((prev) => (tenantFiltersEqual(prev, parsed) ? prev : parsed));
    setSearchInput((prev) => (prev === parsed.search ? prev : parsed.search));
  }, [isGeographyView, searchParams]);

  useEffect(() => {
    if (isGeographyView) {
      return;
    }

    const nextParams = buildTenantsListSearchParams(filters, {
      view:
        searchParams.get("view") === TENANTS_LIST_VIEWS.GEOGRAPHY
          ? TENANTS_LIST_VIEWS.GEOGRAPHY
          : null,
    });

    if (
      serializeTenantsListSearchParams(nextParams) !==
      serializeTenantsListSearchParams(searchParams)
    ) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [filters, isGeographyView, searchParams, setSearchParams]);

  const handleViewChange = useCallback(
    (view) => {
      setSearchParams(
        buildTenantsListSearchParams(filters, { view }),
        { replace: true }
      );
    },
    [filters, setSearchParams]
  );

  const handleClearFilters = useCallback(() => {
    setSearchInput("");
    setFilters(DEFAULT_TENANT_FILTERS);
  }, []);

  const handleSort = useCallback((sortField) => {
    setFilters((prev) => {
      if (prev.sortField === sortField) {
        return {
          ...prev,
          sortOrder: prev.sortOrder === "asc" ? "desc" : "asc",
          page: 1,
        };
      }

      return {
        ...prev,
        sortField,
        sortOrder: "asc",
        page: 1,
      };
    });
  }, []);

  const handleWidgetSelect = useCallback((widgetKey) => {
    setFilters((prev) => applyTenantWidgetPreset(prev, widgetKey));
    if (isGeographyView) {
      handleViewChange(TENANTS_LIST_VIEWS.LIST);
    }
  }, [handleViewChange, isGeographyView]);

  const handleAttentionSelect = useCallback(() => {
    setFilters((prev) => applyTenantWidgetPreset(prev, "trial"));
  }, []);

  const handleEnterTenant = useCallback(
    (target) => {
      enterTenant(target);
    },
    [enterTenant]
  );

  const handleGhostTarget = useCallback((row) => {
    setGhostTarget(row);
  }, []);

  const handleRowClick = useCallback(
    (row) => {
      if (row?.id) {
        navigate(`/tenants/${row.id}`);
      }
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        key: "name",
        label: "Business name",
        sortable: true,
        render: (row) => (
          <div className="min-w-[12rem]">
            <div className="font-medium text-gray-900">{row.name}</div>
            <div className="text-xs text-gray-500">
              {row.owner?.email || row.email || "—"}
            </div>
          </div>
        ),
      },
      {
        key: "abn",
        label: "ABN",
        render: (row) => (
          <span className="font-mono text-xs text-gray-700">{row.abn || "—"}</span>
        ),
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        render: (row) => {
          const badge = getTenantStatusBadge(row.status);
          return <PaStatusBadge label={badge.label} variant={badge.variant} />;
        },
      },
      {
        key: "care_sector",
        label: "Sector",
        render: (row) => formatCareSectorLabel(row.care_sector),
      },
      ...(canViewBilling
        ? [
            {
              key: "billing",
              label: "Subscription & billing",
              render: (row) => {
                const cell = buildTenantListBillingCell(row);

                return (
                  <div className="min-w-[11rem]">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium text-gray-900">{cell.planName}</span>
                      <PaStatusBadge label={cell.stateLabel} variant={cell.stateVariant} />
                    </div>
                    {cell.expiryHint ? (
                      <div className="mt-0.5 text-xs text-gray-500">{cell.expiryHint}</div>
                    ) : null}
                    {cell.outstanding ? (
                      <div className="mt-0.5 text-xs font-medium text-amber-700">
                        Outstanding {cell.outstanding}
                      </div>
                    ) : null}
                  </div>
                );
              },
            },
          ]
        : [
            {
              key: "plan_name",
              label: "Plan",
              render: (row) => (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span>{row.plan_name || "—"}</span>
                  {row.on_trial ? (
                    <PaStatusBadge label="Trial" variant="warning" />
                  ) : null}
                </div>
              ),
            },
          ]),
      {
        key: "participants_count",
        label: "Participants / Workers",
        headerClassName: "text-right",
        cellClassName: "text-right tabular-nums",
        render: (row) => `${row.participants_count} / ${row.workers_count}`,
      },
      {
        key: "created_at",
        label: "Created",
        sortable: true,
        render: (row) => <PaRelativeTime value={row.created_at} />,
      },
      {
        key: "last_login_at",
        label: "Last activity",
        sortable: true,
        render: (row) => <PaRelativeTime value={row.last_login_at} />,
      },
      {
        key: "actions",
        label: "Actions",
        headerClassName: "text-right",
        cellClassName: "text-right",
        render: (row) => (
          <TenantListRowActions
            row={row}
            canEnterTenant={canEnterTenant}
            canStartGhost={canStartGhost}
            tenantContextSpId={tenantContext?.spId}
            onEnterTenant={handleEnterTenant}
            onGhostTarget={handleGhostTarget}
          />
        ),
      },
    ],
    [
      canViewBilling,
      canEnterTenant,
      canStartGhost,
      handleEnterTenant,
      handleGhostTarget,
      tenantContext?.spId,
    ]
  );

  const filterControls = useMemo(
    () => (
      <>
        {TENANT_STATUS_OPTIONS.map((option) => (
          <PaFilterChip
            key={option.value}
            active={filters.status.includes(option.value)}
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                status: toggleFilterValue(prev.status, option.value),
                page: 1,
              }))
            }
          >
            {option.label}
          </PaFilterChip>
        ))}
        <PaFilterChip
          active={filters.onTrial}
          onClick={() =>
            setFilters((prev) => ({
              ...prev,
              onTrial: !prev.onTrial,
              page: 1,
            }))
          }
        >
          Trial
        </PaFilterChip>
        <PaFilterChip
          active={filters.readOnly}
          onClick={() =>
            setFilters((prev) => ({
              ...prev,
              readOnly: !prev.readOnly,
              page: 1,
            }))
          }
        >
          Read-only
        </PaFilterChip>
        <PaFilterChip
          active={filters.billingLeftoverAr}
          onClick={() =>
            setFilters((prev) => ({
              ...prev,
              billingLeftoverAr: !prev.billingLeftoverAr,
              page: 1,
            }))
          }
        >
          Leftover AR
        </PaFilterChip>
        <PaFilterChip
          active={filters.wildcard}
          onClick={() =>
            setFilters((prev) => ({
              ...prev,
              wildcard: !prev.wildcard,
              page: 1,
            }))
          }
        >
          Wildcard
        </PaFilterChip>
        {CARE_SECTOR_OPTIONS.map((option) => (
          <PaFilterChip
            key={option.value}
            active={filters.careSector === option.value}
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                careSector:
                  prev.careSector === option.value ? "" : option.value,
                page: 1,
              }))
            }
          >
            {option.label}
          </PaFilterChip>
        ))}
        {filters.stateCode ? (
          <PaFilterChip
            active
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                stateCode: "",
                suburb: "",
                postcode: "",
                page: 1,
              }))
            }
          >
            State: {filters.stateCode}
          </PaFilterChip>
        ) : null}
        {filters.suburb ? (
          <PaFilterChip
            active
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                suburb: "",
                postcode: "",
                page: 1,
              }))
            }
          >
            Suburb: {filters.suburb}
          </PaFilterChip>
        ) : null}
        {filters.postcode ? (
          <PaFilterChip
            active
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                postcode: "",
                page: 1,
              }))
            }
          >
            Postcode: {filters.postcode}
          </PaFilterChip>
        ) : null}
      </>
    ),
    [
      filters.careSector,
      filters.onTrial,
      filters.postcode,
      filters.readOnly,
      filters.stateCode,
      filters.status,
      filters.suburb,
      filters.wildcard,
      filters.billingLeftoverAr,
    ]
  );

  const pageSubtitle = useMemo(
    () => getTenantsListPageSubtitle(activeView, filters, pagination.total),
    [activeView, filters, pagination.total]
  );

  if (!canViewTenants) {
    return (
      <PaAccessDenied
        title="Tenants"
        subtitle="All platform tenants"
        description="You need platform.tenant.list to view tenants."
      />
    );
  }

  return (
    <PlatformAdminLayout>
      <PaPageHeader title="Tenants" subtitle={pageSubtitle} className="mb-4" />

      <PaTabs
        className="mb-4"
        tabs={TENANTS_LIST_VIEW_OPTIONS}
        activeTab={activeView}
        onChange={handleViewChange}
      />

      {navigationContext ? (
        <div className="mb-4">
          <PaNavigationContextBanner
            title={navigationContext.title}
            description={navigationContext.description}
          />
        </div>
      ) : null}

      <TenantsListSummary
        stats={summaryStats}
        attentionCount={attentionCount}
        loading={summaryLoading}
        filters={filters}
        onWidgetSelect={handleWidgetSelect}
        onAttentionSelect={handleAttentionSelect}
      />

      {isGeographyView ? (
        <>
          {geographyError ? (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {geographyError}
            </div>
          ) : null}
          {stateGeographyError ? (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {stateGeographyError}
            </div>
          ) : null}
          <TenantsGeographyView
            geography={geography}
            stateGeography={stateGeography}
            stateGeographyLoading={stateGeographyLoading}
            selectedStateCode={selectedGeographyState}
            onSelectState={handleSelectGeographyState}
            onClearState={handleClearGeographyState}
            loading={geographyLoading}
            canListTenants={canViewTenants}
            canViewBilling={canViewBilling}
          />
        </>
      ) : (
        <>
      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <PaFilterBar
        className="mb-3"
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Search name, email, or ABN"
        filters={filterControls}
        onClear={
          hasActiveTenantFilters({ ...filters, search: searchInput })
            ? handleClearFilters
            : undefined
        }
        resultCount={pagination.total}
      />

      <PaDataTable
        columns={columns}
        rows={rows}
        rowKey="id"
        sortKey={filters.sortField}
        sortDirection={filters.sortOrder}
        onSort={handleSort}
        onRowClick={handleRowClick}
        isLoading={loading}
        loadingRows={10}
        emptyState={
          <PaEmptyState
            title="No tenants found"
            message="Try adjusting your filters or search"
            action={
              hasActiveTenantFilters(filters) ? (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  Reset to active tenants
                </button>
              ) : null
            }
          />
        }
      />

      <PaPagination
        page={filters.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        rows={rows}
        perPage={filters.perPage}
        loading={loading}
        onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
      />
        </>
      )}

      <GhostStartModal
        open={Boolean(ghostTarget)}
        onClose={() => setGhostTarget(null)}
        title="Ghost as user"
        description="You will operate the portal as this user. Your access is limited to what they can do. All actions are audited."
        targetLabel={
          ghostTarget?.owner?.name
            ? `${ghostTarget.owner.name} (${ghostTarget.owner.email})`
            : ghostTarget?.owner?.email
        }
        submitting={ghostSubmitting}
        allowReadOnly
        onSubmit={async ({ reason, readOnly }) => {
          if (!ghostTarget?.owner?.id) {
            return;
          }

          try {
            await startGhost({
              userId: ghostTarget.owner.id,
              reason,
              readOnly,
              spId: ghostTarget.id,
              actingUser: {
                id: ghostTarget.owner.id,
                name: ghostTarget.owner.name,
                email: ghostTarget.owner.email,
                user_type: ghostTarget.owner.user_type || USER_ROLE.Sp_Admin,
              },
              tenantName: ghostTarget.name,
              tenantFlags: {
                suspended: ghostTarget.status === "suspended",
                read_only: Boolean(ghostTarget.read_only),
              },
            });
            setGhostTarget(null);
          } catch (err) {
            toast.error(err?.message || "Failed to start ghost session");
          }
        }}
      />
    </PlatformAdminLayout>
  );
};

export default TenantsList;
